import { ART_BOX } from "./constants.js";
import { clamp } from "./numeric.js";
import { gallery } from "./retrieve.js";
import { boxPixels } from "./ocrprep.js";
import { bestScore, queryPatches } from "./symbols.js";

/* ===========================================================================
   Verification and decision policy — recognize/verify.py, decide.py, calibration.py.
   =========================================================================== */

export const VERIFY = {
  orbFeatures: 400,
  ratioTest: 0.78,
  ransacReprojThreshold: 6.0,
  minInliers: 12,
  verifyTopN: 8,
  //: Hits further than this behind the retrieval leader skip ORB — see verifyShortlist.
  verifyDistanceGap: 10,
  verifyMin: 3,
  ocrTopN: 4,
  //: The printed set symbol, matched as an edge-template cosine against the bundle's
  //: (set, frame, full-art, rarity) references — VerificationSettings.symbol_min_score.
  //: symbol_min_samples is a build-time knob: buckets below it ship no template. 0.90
  //: sits above the first live false match (a wrong set at 0.883 on a soft frame).
  symbolMinScore: 0.9,
};

//: Ceiling on how many printings a shortlist may expand to — `pipeline.MAX_CANDIDATES`.
//: Popular reprints share one image group with dozens of printings; without a cap a
//: single hit could flood the decision layer.
export const MAX_CANDIDATES = 120;

export const DECISION = {
  acceptThreshold: 0.55,
  margin: 0.06,
  requireDisambiguationForSharedArt: true,
  //: The bar when geometric verification could not run at all — see `retrievalAlone`.
  unverifiedHashFloor: 0.55,
  unverifiedGap: 0.2,
  weightHash: 0.45,
  weightVerify: 0.3,
  weightOcrName: 0.15,
  weightCollector: 0.1,
};

//: The art box in feature-region pixels — verify.py's reference frame for the
//: near-identity check on the fitted homography.
export const FEATURE_REGION = (() => {
  const [x0, y0, x1, y1] = boxPixels(ART_BOX);
  return { x0, y0, width: x1 - x0, height: y1 - y0 };
})();

/**
 * The evidence vector's field order — `calibration.FEATURE_NAMES`, version 2.
 *
 * Load-bearing: the fitted weights are positional, so this list is what a bundle's
 * calibration.json is checked against before its numbers are trusted.
 *
 * The printed identifiers (collector number, set code, set symbol) are deliberately not
 * features: they confirm the set, not the card, so a shortlist full of same-set rivals
 * teaches a fit to weight them *negative* and rank the confirmed printing below its
 * unconfirmed siblings. They act in `decide` instead — as the confirmation gate and the
 * within-group promotion.
 */
export const FEATURE_NAMES = [
  "hash_score",
  "verify_score",
  "inliers",
  "ocr_name_score",
  "with_ocr",
];

/** calibration.candidate_features — the evidence vector, everything in [0, 1]. */
export function candidateFeatures(candidate, withOcr) {
  return [
    candidate.hashScore,
    candidate.verifyScore,
    Math.min(candidate.verifyInliers, 50) / 50,
    candidate.ocrNameScore,
    withOcr ? 1 : 0,
  ];
}

/**
 * Match the printed set symbol against each candidate's edge templates — `_apply_symbol`.
 *
 * The symbol is the one printed identifier that needs no OCR: constant within a set,
 * position known from the frame era. A match confirms the printing exactly the way a
 * read collector number would, and it is the *only* thing that can confirm one on a
 * 1993, 1997 or full-art frame, none of which print a collector number at all. The
 * match feeds `decide`'s confirmation gate and within-group promotion, which are the
 * only printing-level evidence the page has.
 *
 * Costs one offset-grid of edge patches per distinct (frame, full-art) preset in the
 * shortlist — memoised across candidates because a shortlist is usually one frame era —
 * plus a handful of 320-dim dot products per candidate.
 */
export function applySetSymbol(card, candidates) {
  const table = gallery.setSymbols;
  if (!table || !candidates.length) return;
  const patches = new Map();
  for (const candidate of candidates) {
    const printing = candidate.printing;
    const entry = table.get(
      `${printing.setCode}\t${printing.frame || ""}\t${printing.fullArt ? 1 : 0}\t${printing.rarity || ""}`,
    );
    if (!entry || !entry.modes.length) continue;
    const key = `${printing.frame}\t${printing.fullArt ? 1 : 0}`;
    let query = patches.get(key);
    if (query === undefined) {
      query = queryPatches(card, printing.frame, printing.fullArt);
      patches.set(key, query);
    }
    const score = bestScore(query, entry.modes);
    candidate.symbolScore = score;
    candidate.symbolMatch = score >= VERIFY.symbolMinScore;
  }
}

/** decide.fuse_scores — a weighted mean over the signals actually computed. */
export function fuseScores(candidate, withOcr) {
  const weights = [DECISION.weightHash, DECISION.weightVerify];
  const values = [candidate.hashScore, candidate.verifyScore];
  if (withOcr) {
    weights.push(DECISION.weightOcrName);
    values.push(candidate.ocrNameScore);
    if (candidate.collectorMatch != null) {
      weights.push(DECISION.weightCollector);
      values.push(candidate.collectorMatch ? 1 : 0);
    }
  }
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) return 0;
  return weights.reduce((sum, w, i) => sum + w * values[i], 0) / total;
}

/**
 * The hits worth an ORB pass — `pipeline._verify`'s adaptive pool.
 *
 * When the leader is far ahead, the tail of the shortlist is noise and matching
 * against it is pure cost. When the field is close, every hit inside the gap stays —
 * that contested case is what verification is *for*, and shrinking it there would
 * inflate the leader's margin artificially (the false-acceptance path of README
 * section 8). The floor keeps the top contenders verified regardless.
 */
export function verifyShortlist(hits) {
  const head = hits.slice(0, VERIFY.verifyTopN);
  if (!head.length) return head;
  return head.filter(
    (hit, i) => i < VERIFY.verifyMin || hit.distance - head[0].distance <= VERIFY.verifyDistanceGap,
  );
}

/** Whether two candidates are the same picture in different clothes. */
export function sharesArtwork(first, second) {
  if (first.printing.illustration && second.printing.illustration) {
    return first.printing.illustration === second.printing.illustration;
  }
  return first.row === second.row;
}

/**
 * How specifically a printed identifier vouches for this candidate —
 * `decide._identifier_strength`. A collector number names one printing within a set; a
 * set code or set symbol names the set, and the symbol ranks below the code because its
 * dHash match radius admits lookalikes where a read code is exact.
 */
function identifierStrength(candidate) {
  if (candidate.collectorMatch) return 3;
  if (candidate.setCodeMatch) return 2;
  if (candidate.symbolMatch) return 1;
  return 0;
}

/**
 * decide.promote_confirmed — move the printing a printed identifier confirms to the head
 * of its artwork group.
 *
 * Siblings of one artwork tie on every retrieval and geometry signal by construction, so
 * the sort leaves the leader to catalogue order; the identifiers are the only evidence
 * that can tell siblings apart, and they are deliberately not score features. Only
 * near-ties are touched, and an equal-strength tie (two symbols at the same distance)
 * promotes nothing — that is a genuine ambiguity for the shared-art rule to refuse.
 */
export function promoteConfirmed(candidates, margin) {
  const best = candidates[0];
  const group = candidates.filter((c) => sharesArtwork(best, c) && best.score - c.score <= margin);
  if (group.length < 2) return;
  const top = Math.max(...group.map(identifierStrength));
  if (top === 0) return;
  const contenders = group.filter((c) => identifierStrength(c) === top);
  let winner = contenders[0];
  if (contenders.length > 1) {
    // Two collector-number or set-code matches cannot be split here; whichever leads
    // keeps leading. Several symbol matches are split by score — the best wins, and a
    // tie promotes nothing.
    if (top !== 1) return;
    contenders.sort((a, b) => (b.symbolScore ?? 0) - (a.symbolScore ?? 0));
    if (contenders[0].symbolScore === contenders[1].symbolScore) return;
    winner = contenders[0];
  }
  if (winner === best) return;
  candidates.splice(candidates.indexOf(winner), 1);
  candidates.unshift(winner);
}

/**
 * decide.decide — accept, refuse, or hand it back as ambiguous.
 *
 * Three conditions must all hold before a scan is accepted: the fused score clears the
 * threshold, it beats the best candidate *from a different artwork* by a margin, and if
 * the leaders share artwork the winner is confirmed by something printed on the card.
 * Anything else is AMBIGUOUS with the shortlist attached, which is the README's answer
 * to a reprint — surface it, do not guess.
 */
/**
 * decide.retrieval_alone — whether retrieval by itself separates the leader well enough.
 *
 * Only consulted when verification did not run. The evidence is the leader's *separation*
 * rather than its score: measured over 395 hardened synthetic frames on the shipped index,
 * no wrong leader ever led the best different-artwork candidate by more than 0.104, while
 * correct leaders led by a median of 0.677. The score floor is a backstop; the gap does
 * the work.
 */
export function retrievalAlone(candidates) {
  const best = candidates[0];
  if (best.hashScore < DECISION.unverifiedHashFloor) return false;
  const rival = candidates.slice(1).find((c) => !sharesArtwork(best, c));
  // Nothing from another artwork in the shortlist at all: the only open question is which
  // printing of this one, which the shared-art rule judges.
  if (!rival) return true;
  return best.hashScore - rival.hashScore >= DECISION.unverifiedGap;
}

export function decide(candidates, withOcr, verified = true) {
  if (!candidates.length) {
    return { verdict: "REJECTED", candidates, reason: "no candidate within the retrieval radius" };
  }
  for (const candidate of candidates) {
    candidate.score = gallery.calibration
      ? logistic(candidateFeatures(candidate, withOcr), gallery.calibration)
      : fuseScores(candidate, withOcr);
  }
  candidates.sort((a, b) => b.score - a.score);

  // The threshold asks "is the card good enough" and is judged on the group's best
  // score, before promotion can put a confirmed sibling with a hair less of it first.
  //
  // A score computed without verification is not the same quantity: the fit learned its
  // zeros from candidates ORB had *refused*, so an honest read that simply never ran the
  // stage is scored as though it had failed it. Where that happens the leader's separation
  // is consulted instead — never in addition, and never when ORB did run and came back
  // empty, which is the one case a zero really does mean refusal.
  if (
    candidates[0].score < DECISION.acceptThreshold &&
    !(!verified && retrievalAlone(candidates))
  ) {
    return {
      verdict: "REJECTED",
      candidates,
      reason: `best score ${candidates[0].score.toFixed(3)} below threshold ${DECISION.acceptThreshold.toFixed(2)}`,
    };
  }
  if (candidates.length > 1) promoteConfirmed(candidates, DECISION.margin);

  const best = candidates[0],
    runnerUp = candidates[1] || null;

  const confirmed = !!best.collectorMatch || !!best.setCodeMatch || !!best.symbolMatch;
  const rival = candidates.slice(1).find((c) => !sharesArtwork(best, c));
  if (rival && best.score - rival.score < DECISION.margin && !confirmed) {
    return {
      verdict: "AMBIGUOUS",
      candidates,
      reason: "top candidates from different artworks are too close to separate",
    };
  }
  if (
    DECISION.requireDisambiguationForSharedArt &&
    runnerUp &&
    sharesArtwork(best, runnerUp) &&
    !confirmed
  ) {
    return {
      verdict: "AMBIGUOUS",
      candidates,
      reason: "printing-level identity unresolved: shared artwork, no printed identifiers read",
    };
  }
  return { verdict: "ACCEPTED", candidates, reason: "" };
}

/** calibration.Calibration.probability — the fitted logistic, clipped like numpy's. */
export function logistic(features, calibration) {
  let z = calibration.bias;
  for (let i = 0; i < features.length; i++) z += features[i] * calibration.weights[i];
  return 1 / (1 + Math.exp(-clamp(z, -30, 30)));
}

/** Scryfall's `normal` image, rebuilt from the id — the same URL minus its cache-buster. */
export function imageUrl(printing) {
  const id = printing.scryfall;
  return `https://cards.scryfall.io/normal/${printing.face ? "back" : "front"}/${id[0]}/${id[1]}/${id}.jpg`;
}
